import "./TeamDetail.css";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import Card, { CardHead } from "../../components/Card/Card";
import Avatar from "../../components/Avatar/Avatar";
import TextField from "../../components/TextField/TextField";
import Button from "../../components/Button/Button";
import ConfirmAction from "../../components/ConfirmAction/ConfirmAction";
import PreviewFrame from "../../components/PreviewFrame/PreviewFrame";
import ReviewsPreview from "../../preview/participant/components/Reviews/Reviews";
import TeamPanelPreview from "../../preview/participant/components/TeamPanel/TeamPanel";
import EliminationBannerPreview from "../../preview/participant/components/EliminationBanner/EliminationBanner";
import SubmissionWindowPreview from "../../preview/participant/components/SubmissionWindow/SubmissionWindow";
import Loader from "../../components/Loader/Loader";

const NOOP_API = { leaveTeam: async () => {}, createTeam: async () => {}, joinTeam: async () => {} };

function fmt(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function TeamDetail() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();

  const [team, setTeam] = useState(null);
  const [error, setError] = useState("");

  const [reviewBody, setReviewBody] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState("");

  const [renameDraft, setRenameDraft] = useState("");

  const [setSlot, setSetSlot] = useState("");
  const [setUrl, setSetUrl] = useState("");
  const [setKind, setSetKind] = useState("github");

  const load = useCallback(() => {
    api
      .get(`/admin/team/${teamId}`)
      .then((data) => {
        setTeam(data);
        setRenameDraft(data.name);
      })
      .catch((e) => setError(e.message));
  }, [teamId]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <p className="t-body-sm teams-error" role="alert">{error}</p>;
  if (!team) return <Loader inline />;

  const eliminationStatus = team.elimination ? team.elimination.status : "active";

  async function postReview() {
    setReviewBusy(true);
    setReviewError("");
    try {
      await api.post("/admin/remark", { teamId, body: reviewBody });
      setReviewBody("");
      load();
    } catch (e) {
      setReviewError(e.message);
      throw e;
    } finally {
      setReviewBusy(false);
    }
  }

  const previewReviews = reviewBody.trim()
    ? [{ id: "draft", author: "You", avatarUrl: null, body: reviewBody, at: "Just now" }]
    : team.remarks.map((r) => ({ id: r.id, author: r.author, avatarUrl: r.avatarUrl, body: r.body, at: fmt(r.createdAt) }));

  return (
    <div className="team-detail col">
      <button type="button" className="team-detail-back t-body-sm" onClick={() => navigate("/")}>
        ← All teams
      </button>

      <div className="team-detail-head row">
        <div className="col">
          <div className="row team-detail-title-row">
            <h1 className="t-h1">{team.name}</h1>
            <span className={`team-status-tag team-status-tag-${eliminationStatus}`}>
              {eliminationStatus === "active"
                ? "Active"
                : eliminationStatus === "pre_elimination"
                  ? "Pre-elimination"
                  : "Eliminated"}
            </span>
          </div>
          <span className="t-mono t-dimmer">{team.joinCode}</span>
        </div>
      </div>

      <div className="team-detail-grid">
        <div className="team-detail-main col">
          <Card>
            <CardHead eyebrow="Roster" title="Members" />
            <ul className="team-detail-members col">
              {team.members.map((m) => (
                <li key={m.id} className="team-detail-member row">
                  <Avatar src={m.avatarUrl} name={m.name} size="sm" />
                  <span className="t-label">{m.name}</span>
                  <span className="t-mono t-dimmer">{m.email}</span>
                  {isSuperAdmin ? (
                    <ConfirmAction
                      small
                      actionLabel="Remove"
                      confirmLabel="Remove member"
                      confirmWord="DELETE"
                      danger
                      summary={`This removes ${m.name} from ${team.name}. This cannot be undone.`}
                      onConfirm={async () => {
                        await api.delete(`/admin/team/${teamId}/member/${m.id}`);
                        load();
                      }}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <CardHead eyebrow="On record" title="Submissions" />
            {team.submissions.length ? (
              <ul className="team-detail-submissions col">
                {team.submissions.map((s) => (
                  <li key={s.slot} className="team-detail-submission col">
                    <div className="row team-detail-submission-top">
                      <span className="t-h4">{s.title}</span>
                      {s.setByAdmin ? <span className="team-admin-tag t-mono">set by admin</span> : null}
                    </div>
                    <a className="t-mono team-detail-link" href={s.url} target="_blank" rel="noreferrer noopener">
                      {s.url}
                    </a>
                    {Object.keys(s.answers || {}).length ? (
                      <span className="t-body-sm t-dimmer">
                        {Object.entries(s.answers).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                      </span>
                    ) : null}
                    <span className="t-mono t-dimmer">{fmt(s.updatedAt)} · {s.submittedBy}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="t-body-sm t-dimmer">Nothing submitted yet.</p>
            )}
          </Card>

          <Card>
            <CardHead
              eyebrow="From mentors"
              title="Reviews"
              description="Timestamped only, not attached to any submission."
            />
            <div className="team-detail-review-composer col">
              <label className="t-label" htmlFor="review-body">Post a review</label>
              <textarea
                id="review-body"
                className="team-detail-textarea"
                rows={4}
                value={reviewBody}
                onChange={(e) => setReviewBody(e.target.value)}
                maxLength={1000}
                placeholder="What did you see in their work?"
              />
              <div className="row team-detail-review-actions">
                <span className="t-body-sm t-dimmer">{reviewBody.length}/1000</span>
                {isSuperAdmin ? (
                  <ConfirmAction
                    small
                    actionLabel="Post review"
                    confirmLabel="Post it"
                    summary={`This posts your review to ${team.name}. They see it as soon as reviews are visible.`}
                    consequence="A review cannot be edited or taken down from here once it is posted."
                    disabled={!reviewBody.trim()}
                    onConfirm={postReview}
                  />
                ) : (
                  <Button
                    small
                    busy={reviewBusy}
                    busyLabel="Posting…"
                    disabled={!reviewBody.trim()}
                    onClick={postReview}
                  >
                    Post review
                  </Button>
                )}
              </div>
              {reviewError ? <p className="t-body-sm team-error-text" role="alert">{reviewError}</p> : null}
            </div>

            <PreviewFrame>
              <ReviewsPreview reviews={previewReviews} showAvatars />
            </PreviewFrame>
          </Card>
        </div>

        <aside className="team-detail-side col">
          {isSuperAdmin ? (
            <>
              <Card>
                <CardHead eyebrow="Super admin" title="Rename team" />
                <TextField
                  label="Team name"
                  value={renameDraft}
                  onChange={setRenameDraft}
                />
                <ConfirmAction
                  actionLabel="Rename"
                  confirmLabel="Rename team"
                  confirmWord={team.name}
                  summary={`This renames "${team.name}" to "${renameDraft}".`}
                  disabled={!renameDraft.trim() || renameDraft.trim() === team.name}
                  onConfirm={async () => {
                    await api.patch(`/admin/team/${teamId}`, { name: renameDraft.trim() });
                    load();
                  }}
                />
                <PreviewFrame>
                  <TeamPanelPreview
                    team={{ ...team, code: team.joinCode, members: team.members }}
                    user={team.members[0] || { email: "" }}
                    api={NOOP_API}
                    rosterOpen
                    closesLabel=""
                    showAvatars
                  />
                </PreviewFrame>
              </Card>

              <Card>
                <CardHead eyebrow="Super admin" title="Elimination" />
                <p className="t-body-sm t-dim">
                  {eliminationStatus === "active" && "Active. Marking stages the team in pre-elimination, invisible to them, until confirmed."}
                  {eliminationStatus === "pre_elimination" && "Staged for elimination, not yet confirmed. The team still sees itself as active."}
                  {eliminationStatus === "eliminated" && "Eliminated. The team sees the farewell banner and no submission windows."}
                </p>
                <div className="row team-detail-elimination-actions">
                  {eliminationStatus === "active" ? (
                    <ConfirmAction
                      small
                      actionLabel="Mark for elimination"
                      confirmLabel="Mark"
                      confirmWord={team.name}
                      summary={`This stages ${team.name} for elimination. They see nothing different until you confirm.`}
                      onConfirm={async () => {
                        await api.post(`/admin/team/${teamId}/elimination`, { action: "mark", reason: "" });
                        load();
                      }}
                    />
                  ) : null}
                  {eliminationStatus === "pre_elimination" ? (
                    <>
                      <ConfirmAction
                        small
                        danger
                        actionLabel="Confirm elimination"
                        confirmLabel="Confirm"
                        confirmWord={team.name}
                        summary={`This eliminates ${team.name}. They will see the farewell banner and lose access to every submission window.`}
                        onConfirm={async () => {
                          await api.post(`/admin/team/${teamId}/elimination`, { action: "confirm" });
                          load();
                        }}
                      />
                      <ConfirmAction
                        small
                        actionLabel="Clear"
                        confirmLabel="Clear"
                        confirmWord="CONFIRM"
                        summary={`This clears the pre-elimination on ${team.name} and returns them to active.`}
                        onConfirm={async () => {
                          await api.post(`/admin/team/${teamId}/elimination`, { action: "clear" });
                          load();
                        }}
                      />
                    </>
                  ) : null}
                  {eliminationStatus === "eliminated" ? (
                    <ConfirmAction
                      small
                      actionLabel="Reinstate team"
                      confirmLabel="Reinstate"
                      confirmWord="CONFIRM"
                      summary={`This clears the elimination on ${team.name}. They will see everything as normal again, with no trace of it.`}
                      onConfirm={async () => {
                        await api.post(`/admin/team/${teamId}/elimination`, { action: "clear" });
                        load();
                      }}
                    />
                  ) : null}
                </div>
                <PreviewFrame>
                  {eliminationStatus === "eliminated" ? (
                    <EliminationBannerPreview teamName={team.name} />
                  ) : (
                    <p className="t-body-sm t-dimmer">
                      Nothing changes on their dashboard while active or in pre-elimination.
                    </p>
                  )}
                </PreviewFrame>
              </Card>

              <Card>
                <CardHead
                  eyebrow="Super admin"
                  title="Set a submission by hand"
                  description="Skips the link check entirely. Recorded as set by an admin, in the open."
                />
                <TextField label="Window slug" value={setSlot} onChange={setSetSlot} placeholder="r1_deck" mono />
                <TextField label="URL" value={setUrl} onChange={setSetUrl} placeholder="https://…" mono />
                <div className="row team-detail-kind-toggle">
                  {["github", "pptx"].map((k) => (
                    <button
                      key={k}
                      type="button"
                      className={`team-kind-btn${setKind === k ? " team-kind-btn-on" : ""}`}
                      onClick={() => setSetKind(k)}
                    >
                      {k === "github" ? "Repository" : "Slides"}
                    </button>
                  ))}
                </div>
                <ConfirmAction
                  actionLabel="Set submission"
                  confirmLabel="Set submission"
                  confirmWord="CONFIRM"
                  disabled={!setSlot.trim() || !setUrl.trim()}
                  summary={`This sets "${setSlot}" for ${team.name} to the URL below, bypassing the link check.`}
                  onConfirm={async () => {
                    await api.put(`/admin/team/${teamId}/submission/${setSlot.trim()}`, { url: setUrl.trim() });
                    setSetSlot("");
                    setSetUrl("");
                    load();
                  }}
                />
                <PreviewFrame>
                  <SubmissionWindowPreview
                    window={{
                      slot: setSlot || "slot",
                      kind: setKind,
                      title: setSlot || "Submission",
                      badge: setKind === "github" ? "GITHUB" : "PPTX",
                      cta: "Submit",
                      label: setKind === "github" ? "GitHub repository" : "Google Slides link",
                      inputs: [],
                    }}
                    value={
                      setUrl
                        ? { url: setUrl, answers: {}, at: "Just now", by: "Admin" }
                        : null
                    }
                    remaining={null}
                    note="Nothing to submit"
                  />
                </PreviewFrame>
              </Card>

              <Card>
                <CardHead eyebrow="Super admin" title="Danger zone" />
                <ConfirmAction
                  danger
                  actionLabel="Delete team"
                  confirmLabel="Delete team"
                  confirmWord="DELETE"
                  summary={`This deletes ${team.name}, its ${team.members.length} member${team.members.length === 1 ? "" : "s"}' membership, ${team.submissions.length} submission${team.submissions.length === 1 ? "" : "s"} and ${team.remarks.length} mentor review${team.remarks.length === 1 ? "" : "s"}. This cannot be undone.`}
                  onConfirm={async () => {
                    await api.delete(`/admin/team/${teamId}`);
                    navigate("/");
                  }}
                />
              </Card>
            </>
          ) : isSuperAdmin ? (
            <Card>
              <CardHead eyebrow="Super admin" title="Locked" description="Enter the super admin password on the Operations page to make changes here." />
            </Card>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
